<template>
  <div class="green-operation">
    <GreenOperationHistoricalHistory
      v-if="showHistoricalHistory"
      @back="showHistoricalHistory = false"
    />
    <template v-else>
      <div class="green-operation__actions">
        <a
          v-if="canViewHistoricalRecords"
          class="green-operation__history-link"
          @click="showHistoricalHistory = true"
        >
          {{ $t('tenancy.greenOperation.viewHistoricalHistory') }}
        </a>
        <GreenOperationActionBtns />
      </div>
      <SppStatusInfo />
      <div
        v-if="shouldShowPreviousGreenOperationDisplay"
        class="row"
      >
        <HKLCard
          class="small-6 green-operation__documents"
          :title="$t('tenancy.greenOperation.tab.sustainabilityDocuments')"
        >
          <GreenOperationDocuments />
          <GreenOperationDocumentsHistory />
        </HKLCard>
        <HKLCard
          class="small-6 green-operation__guideline"
          :title="$t('tenancy.greenOperation.tab.guideline')"
        >
          <PostFitOutGuideline
            class="card-mode"
            :guidelines="publishedGuidelines"
            :expand="true"
          />
        </HKLCard>
      </div>
      <HKLCard
        v-else
        :title="$t('tenancy.greenOperation.tab.guideline')"
      >
        <PostFitOutGuideline
          class="card-mode"
          :guidelines="publishedGuidelines"
          :expand="true"
        />
      </HKLCard>
    </template>
  </div>
</template>

<script>
import { ref, unref, computed } from 'vue-demi'
import HKLCard from '@/controls/Card/HKLCard.vue'
import { useAttachments } from '@/controls/Upload/hook'
import PostFitOutGuideline from '@/modules/tenantProjects/postFitOutLetter/PostFitOutGuideline.vue'
import { useStoreGetters } from '@/store/index'
import { useHasTenantGreenOperationRight } from '@/pages/ccd/tenancy.hook'
import { sortGuidelines } from '@/modules/tenantProjects/greenFitOut/utils'
import SppStatusInfo from './SppStatusInfo.vue'
import GreenOperationActionBtns from './GreenOperationActionBtns.vue'
import GreenOperationDocuments from './GreenOperationDocuments.vue'
import GreenOperationDocumentsHistory from './GreenOperationDocumentsHistory.vue'
import GreenOperationHistoricalHistory from './GreenOperationHistoricalHistory.vue'
import { isExcluded } from './GreenOperationDocuments.utils'
import { useGreenOperation, useGreenOperationGuidelines, useGreenOperationPerms, useGreenOperationStatus } from './hook'
import { useGreenOperationSppStatus } from './GreenOperationSpp.hook'
export default {
  components: {
    HKLCard,
    PostFitOutGuideline,
    SppStatusInfo,
    GreenOperationActionBtns,
    GreenOperationDocuments,
    GreenOperationDocumentsHistory,
    GreenOperationHistoricalHistory,
  },
  setup () {
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const hasTenantGreenOperationRight = useHasTenantGreenOperationRight()
    const showHistoricalHistory = ref(false)
    const { data: greenOperation } = useGreenOperation()
    const greenOperationId = computed(() => unref(greenOperation).id)
    const { data: files } = useAttachments(greenOperationId)
    const hasAssessment = computed(() => (unref(greenOperation).assessments || []).length > 0)
    const { isStarted } = useGreenOperationSppStatus()
    const { status: greenOperationStatus, isNew: isGreenOperationNew } = useGreenOperationStatus()
    const { canStart, canPreview, canViewPoint } = useGreenOperationPerms()
    const { data: guidelineObj } = useGreenOperationGuidelines()
    const publishedGuidelines = computed(() => sortGuidelines(unref(guidelineObj).guidelines))
    const shouldShowPreviousGreenOperationDisplay = computed(() => {
      return unref(isStarted)
    })

    return {
      isStarted,
      shouldShowPreviousGreenOperationDisplay,
      showHistoricalHistory,
      publishedGuidelines,
      canViewHistoricalRecords: computed(() => {
        const hasViewRight = unref(isTenant)
          ? unref(hasTenantGreenOperationRight)
          : unref(canStart) || unref(canPreview) || unref(canViewPoint)
        const hasExcludedDocument = unref(files).some(isExcluded)
        return [
          unref(greenOperationStatus),
          !unref(isGreenOperationNew) || hasExcludedDocument || unref(hasAssessment),
          hasViewRight,
        ].every(Boolean)
      }),
    }
  },
}
</script>

<style lang="scss" scoped>
.green-operation {
  ::v-deep .hkl-card_header.small-12 {
    text-align: left;
  }
}

.green-operation__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 10px;
}

.green-operation__history-link {
  cursor: pointer;
  font-weight: bold;
  text-decoration: underline;
}

.green-operation__documents {
  padding-right: 10px;
  max-width: 50% !important;
  & > ::v-deep .hkl-card_header {
    font-size: 14px;
    text-align: left;
  }
  & + .green-operation__guideline {
    padding-left: 10px;
  }
}

.green-operation__guideline {
  & > ::v-deep .hkl-card_header {
    font-size: 14px;
  }
}
</style>
